import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/api/query-keys";
import type {
	CreatedDistrict,
	MqttReadingDetailParams,
	MqttReadingsParams,
	MqttSource,
	MqttSourcesParams,
	Reading,
	ReadingDetail,
} from "@/api/reseller/types";
import type { ListParams, PaginatedResponse } from "@/api/types";
import { useEffectiveRole } from "@/hooks/use-effective-role";
import { api } from "@/lib/api";
import type {
	District,
	DistrictDashboardData,
	DistrictManagerProfile,
} from "./types";

export function useDistricts(): UseQueryResult<District[], Error> {
	const role = useEffectiveRole();
	const endpoint =
		role === "district_manager"
			? "/api/districts/"
			: role === "reseller"
				? "/api/reseller/created_districts/"
				: null;

	return useQuery({
		queryKey: queryKeys.districts.list(),
		queryFn: async (): Promise<District[]> => {
			if (role === "reseller") {
				const res = await api.get<PaginatedResponse<CreatedDistrict>>(
					"/api/reseller/created_districts/",
					{ params: { page_size: 100 } },
				);
				return res.data.results.map((d) => ({
					id: d.id,
					name: d.name,
					apartments_count: 0,
					meters_count: 0,
				}));
			}
			const res = await api.get<PaginatedResponse<District>>(
				"/api/districts/",
				{
					params: { page_size: 100 },
				},
			);
			return res.data.results;
		},
		enabled: !!endpoint,
	});
}

export function useDistrictsList(
	params: ListParams = {},
): UseQueryResult<PaginatedResponse<District>, Error> {
	return useQuery({
		queryKey: queryKeys.districts.list(params),
		queryFn: async (): Promise<PaginatedResponse<District>> => {
			const res = await api.get<PaginatedResponse<District>>(
				"/api/districts/",
				{ params },
			);
			return res.data;
		},
	});
}

type DashboardParams = {
	district_id?: number | null;
	start_date?: string;
	end_date?: string;
};

export function useDistrictDashboard(
	params?: DashboardParams,
): UseQueryResult<DistrictDashboardData, Error> {
	const cleanParams = params
		? Object.fromEntries(
				Object.entries(params).filter(([, v]) => v != null && v !== ""),
			)
		: {};
	return useQuery({
		queryKey: queryKeys.districts.dashboard(cleanParams),
		queryFn: async (): Promise<DistrictDashboardData> => {
			const res = await api.get<DistrictDashboardData>(
				"/api/districts/dashboard/",
				{ params: cleanParams },
			);
			return res.data;
		},
	});
}

export function useDistrictManagerProfile(): UseQueryResult<
	DistrictManagerProfile,
	Error
> {
	return useQuery({
		queryKey: queryKeys.districts.myProfile(),
		queryFn: async (): Promise<DistrictManagerProfile> => {
			const res = await api.get<DistrictManagerProfile>(
				"/api/districts/my-profile/",
			);
			return res.data;
		},
	});
}

export function useDmMqttSources(
	params: MqttSourcesParams = {},
): UseQueryResult<PaginatedResponse<MqttSource>, Error> {
	return useQuery({
		queryKey: queryKeys.districts.mqttSources(params),
		queryFn: async (): Promise<PaginatedResponse<MqttSource>> => {
			const res = await api.get<PaginatedResponse<MqttSource>>(
				"/api/districts/my_sources/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useDmReadings(
	params: MqttReadingsParams = {},
): UseQueryResult<PaginatedResponse<Reading>, Error> {
	return useQuery({
		queryKey: queryKeys.districts.readings(params),
		queryFn: async (): Promise<PaginatedResponse<Reading>> => {
			const res = await api.get<PaginatedResponse<Reading>>(
				"/api/districts/readings/",
				{ params },
			);
			return res.data;
		},
	});
}

export function useDmReadingDetail(
	readingId: number,
	params: MqttReadingDetailParams = {},
	options: { enabled?: boolean } = {},
): UseQueryResult<ReadingDetail, Error> {
	return useQuery({
		queryKey: [...queryKeys.districts.readingDetail(readingId), params],
		queryFn: async (): Promise<ReadingDetail> => {
			const res = await api.get<ReadingDetail>(
				`/api/districts/readings/${readingId}/`,
				{ params },
			);
			return res.data;
		},
		enabled: !!readingId && (options.enabled ?? true),
	});
}
